import { Box, Button, Heading, Input, Link, Text, Wrap } from '@chakra-ui/react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  FAQ_CATEGORIES,
  FAQ_DISCORD_INVITE_URL,
  FAQ_ITEMS,
  type FaqCategory,
} from '../data/faqData';
import { FaqAccordion, type FaqAccordionItem } from './FaqAccordion';

type ResolvedItem = FaqAccordionItem & { category: FaqCategory; haystack: string };

const SEARCH_PLACEHOLDER = msg({ id: 'faq.page.search.placeholder', message: 'Search questions…' });

const readHash = () => {
  if (typeof window === 'undefined') return null;
  const raw = window.location.hash.replace(/^#/, '');
  return raw ? decodeURIComponent(raw) : null;
};

export const FaqPageContent = () => {
  const { _: translate } = useLingui();
  const [category, setCategory] = useState<FaqCategory | 'all'>('all');
  const [query, setQuery] = useState('');
  // `undefined` = initial (first item open); a string/null tracks user/hash choice.
  const [openKey, setOpenKey] = useState<string | null | undefined>(undefined);

  const allItems = useMemo<ResolvedItem[]>(
    () =>
      FAQ_ITEMS.map((item) => {
        const question = translate(item.question);
        const answer = translate(item.answer);
        return {
          key: item.id,
          anchorId: item.id,
          category: item.category,
          question,
          answer,
          haystack: `${question}\n${answer}`.toLowerCase(),
          link: item.link ? { label: translate(item.link.label), href: item.link.href } : undefined,
        };
      }),
    [translate]
  );

  const normalizedQuery = query.trim().toLowerCase();

  const visibleItems = useMemo(() => {
    if (normalizedQuery) {
      return allItems.filter((item) => item.haystack.includes(normalizedQuery));
    }
    return category === 'all' ? allItems : allItems.filter((item) => item.category === category);
  }, [allItems, category, normalizedQuery]);

  // Open the question referenced by the URL hash (deep link) on mount and on change.
  useEffect(() => {
    const applyHash = () => {
      const hash = readHash();
      if (!hash || !FAQ_ITEMS.some((item) => item.id === hash)) return;
      setCategory('all');
      setQuery('');
      setOpenKey(hash);
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  const openedKey = openKey === undefined ? (visibleItems[0]?.key ?? null) : openKey;

  const handleToggle = useCallback(
    (key: string) => {
      const next = openedKey === key ? null : key;
      setOpenKey(next);
      if (typeof window !== 'undefined') {
        const url = next ? `#${encodeURIComponent(next)}` : window.location.pathname;
        window.history.replaceState(null, '', url);
      }
    },
    [openedKey]
  );

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: allItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    }),
    [allItems]
  );

  return (
    <Box
      as="section"
      aria-label="FAQ"
      maxW="880px"
      mx="auto"
      px={{ base: 4, md: 6 }}
      py={{ base: 10, md: 16 }}
    >
      {/* FAQPage structured data — canonical FAQ schema lives here */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Box textAlign="center" mb={{ base: 8, md: 10 }}>
        <Text
          textStyle="badge"
          color="purple.300"
          textTransform="uppercase"
          letterSpacing="0.15em"
          mb={3}
        >
          FAQ
        </Text>
        <Heading
          as="h1"
          fontWeight="900"
          lineHeight="1.05"
          fontSize={{ base: '3xl', md: '5xl' }}
          textTransform="uppercase"
          letterSpacing="-0.02em"
          mb={4}
        >
          <Trans id="faq.page.title">Frequently asked questions</Trans>
        </Heading>
        <Text color="fg.muted" textStyle={{ base: 'sm', md: 'md' }} maxW="560px" mx="auto">
          <Trans id="faq.page.subtitle">
            Everything you need to know about Teamplay.gg. Still stuck?
          </Trans>{' '}
          <Link
            href={FAQ_DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            color="purple.300"
            fontWeight="600"
            _hover={{ color: 'purple.200', textDecoration: 'underline' }}
          >
            <Trans id="faq.page.subtitle.cta">Chat with our team on Discord</Trans>
          </Link>
        </Text>
      </Box>

      <Box maxW="560px" mx="auto" mb={{ base: 6, md: 8 }}>
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={translate(SEARCH_PLACEHOLDER)}
          size="lg"
          bg="whiteAlpha.50"
          borderColor="whiteAlpha.200"
          borderRadius="full"
          _placeholder={{ color: 'fg.muted' }}
          _hover={{ borderColor: 'whiteAlpha.300' }}
          _focusVisible={{ borderColor: 'purple.400', boxShadow: 'none' }}
        />
      </Box>

      {!normalizedQuery && (
        <Wrap justify="center" gap={2} mb={{ base: 8, md: 10 }}>
          {FAQ_CATEGORIES.map((option) => {
            const isActive = category === option.value;
            return (
              <Button
                key={option.value}
                size="sm"
                variant={isActive ? 'solid' : 'outline'}
                borderRadius="full"
                colorPalette={isActive ? 'purple' : 'gray'}
                color={isActive ? undefined : 'fg.muted'}
                borderColor="whiteAlpha.200"
                onClick={() => {
                  setCategory(option.value);
                  setOpenKey(undefined);
                }}
              >
                {translate(option.label)}
              </Button>
            );
          })}
        </Wrap>
      )}

      <Box maxW="800px" mx="auto">
        {visibleItems.length > 0 ? (
          <FaqAccordion items={visibleItems} openKey={openedKey} onToggle={handleToggle} />
        ) : (
          <Text textAlign="center" color="fg.muted" py={8}>
            <Trans id="faq.page.search.empty">
              No questions matched your search. Try different keywords or ask us on Discord.
            </Trans>
          </Text>
        )}
      </Box>
    </Box>
  );
};
